/**
 * Getting out of the player's way.
 *
 * Someone the player runs (or drives) at reads where the player will pass
 * closest to them, and how soon. Close and soon, they step aside, away from
 * the player's line, as a person does: after a moment to see it coming, and
 * no faster than legs carry anyone sideways (`DODGE_SPEED`). Too late to get
 * clear, they flinch: arms up, leaning away (`brace`). So a car driven at
 * someone from far enough off misses them, and one that comes on fast, or
 * turns at them, does not.
 */

import type { GaitPose } from './gait.js';

/** Faster than this, m/s, the player is running at someone rather than walking up. */
const RUNNING = 2.5;
/** How soon, seconds, and how close, metres, a pass must be to dodge. */
const SOON = 1.6;
const CLOSE = 1.5;
/** How soon, seconds, and how close, metres, a pass must be to flinch at. */
const FLINCH_SOON = 0.7;
const FLINCH_CLOSE = 1.3;

/** The fastest anyone gets sideways, leaping out of the way, m/s; and stepping over for someone, m/s. */
export const DODGE_SPEED = 3;
export const SIDESTEP_SPEED = 1;
/** How long it takes to see something coming and move, seconds: from the quickest to the slowest. */
export const REACTION = [0.3, 0.7] as const;

/**
 * Where the player coming at (vx, vz) m/s passes someone at offset (rx, rz)
 * from them: how soon, seconds, and the offset then. Undefined when the
 * player is not running at them, or is going away.
 */
export function passing(rx: number, rz: number, vx: number, vz: number): { t: number; mx: number; mz: number } | undefined {
  const v2 = vx * vx + vz * vz;
  if (v2 < RUNNING * RUNNING) return undefined;
  const t = (rx * vx + rz * vz) / v2;
  if (t <= 0) return undefined;
  return { t, mx: rx - vx * t, mz: rz - vz * t };
}

/**
 * Which way across to jump, from someone at offset (rx, rz) from the player
 * coming at (vx, vz) m/s, along their own sideways axis (ax, az): +1 or -1,
 * or 0 when there is no need. Dead on the player's line, `tie` decides.
 */
export function dodgeSide(rx: number, rz: number, vx: number, vz: number, ax: number, az: number, tie: 1 | -1): -1 | 0 | 1 {
  const pass = passing(rx, rz, vx, vz);
  if (pass === undefined || pass.t > SOON || Math.hypot(pass.mx, pass.mz) > CLOSE) return 0;
  const side = pass.mx * ax + pass.mz * az;
  return Math.abs(side) > 0.05 ? (side > 0 ? 1 : -1) : tie;
}

/** Whether someone at offset (rx, rz) from the player coming at (vx, vz) is about to be run into: too late to get clear. */
export function flinches(rx: number, rz: number, vx: number, vz: number): boolean {
  const pass = passing(rx, rz, vx, vz);
  return pass !== undefined && pass.t < FLINCH_SOON && Math.hypot(pass.mx, pass.mz) < FLINCH_CLOSE;
}

/** Flinching: forearms up before the face, elbows bent hard, leaning back from it, chin down. */
const FLINCH: Pick<GaitPose, 'lean' | 'nod' | 'spread'> & { shoulder: number; elbow: number; knee: number } = {
  lean: -0.22, nod: 0.3, spread: 0.25, shoulder: 1.25, elbow: 2.0, knee: 0.35,
};

/** `pose`, flinching by `k` (0 not at all, 1 fully), in place. */
export function brace(pose: GaitPose, k: number): GaitPose {
  if (k <= 0) return pose;
  const mix = (a: number, b: number) => a + (b - a) * k;
  pose.lean = mix(pose.lean, FLINCH.lean);
  pose.nod = mix(pose.nod, FLINCH.nod);
  pose.spread = mix(pose.spread, FLINCH.spread);
  pose.shoulder.left = mix(pose.shoulder.left, FLINCH.shoulder);
  pose.shoulder.right = mix(pose.shoulder.right, FLINCH.shoulder);
  pose.elbow.left = mix(pose.elbow.left, FLINCH.elbow);
  pose.elbow.right = mix(pose.elbow.right, FLINCH.elbow);
  // Knees give a little, bracing for it.
  pose.knee.left = Math.max(pose.knee.left, FLINCH.knee * k);
  pose.knee.right = Math.max(pose.knee.right, FLINCH.knee * k);
  pose.bob -= 0.06 * k;
  return pose;
}
